import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { ChangeRequest } from '../../data/types';
import { date, units as formatUnits } from '../../lib/format';
import { CHANGE_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, KeyValueGrid, PageHeader, StatusChip } from '../../ui/primitives';

const NEEDS_APPROVAL = new Set(['BANK_CHANGE', 'TRANSFER']);

function RequestDrawer({
  request,
  onClose,
}: {
  request: ChangeRequest | null;
  onClose: () => void;
}) {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  if (!request) return <Drawer anchor="right" open={false} onClose={onClose} />;
  const holder = scope.holderById.get(request.holderId);
  const to = request.toHolderId ? scope.holderById.get(request.toHolderId) : undefined;
  const advance = (status: ChangeRequest['status'], message: string) => {
    dispatch({ type: 'CHANGE_REQUEST_ADVANCE', id: request.id, status });
    toast(message);
    onClose();
  };
  const open = request.status === 'SUBMITTED' || request.status === 'VERIFYING';
  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="caption" color="text.secondary">
          {request.reference} · via {request.channel.replace('_', ' ').toLowerCase()}
        </Typography>
        <Typography variant="h5" sx={{ mt: 0.5 }}>
          {CHANGE_LABELS[request.kind]}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <StatusChip status={request.status} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <KeyValueGrid
            columns={1}
            items={[
              { label: 'Holder', value: holder?.name },
              ...(to ? [{ label: 'Transfer to', value: to.name }] : []),
              ...(request.fundId
                ? [{ label: 'Fund', value: `${scope.fundById.get(request.fundId)?.name}` }]
                : []),
              ...(request.units ? [{ label: 'Units', value: formatUnits(request.units) }] : []),
              { label: 'Submitted', value: date(request.submittedAt) },
              ...request.details.map((detail) => ({ label: detail.label, value: detail.value })),
            ]}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Verification</Typography>
        <List dense>
          {request.verification.map((item) => (
            <ListItem key={item} disableGutters>
              <ListItemText primary={`• ${item}`} />
            </ListItem>
          ))}
        </List>
        {request.kind === 'BANK_CHANGE' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bank changes are a common fraud vector. Confirm by call-back to the registered phone
            number before approval; payments are held for 48 hours after a change.
          </Alert>
        )}
        {open && (
          <Stack useFlexGap direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {request.status === 'SUBMITTED' && (
              <Button
                variant="outlined"
                onClick={() => advance('VERIFYING', 'Moved to verification')}
              >
                Start verification
              </Button>
            )}
            {request.kind === 'REDEMPTION' ? (
              <Button
                variant="contained"
                onClick={() =>
                  advance('QUEUED', 'Verified and queued for the next redemption window')
                }
              >
                Verified — queue for window
              </Button>
            ) : NEEDS_APPROVAL.has(request.kind) ? (
              <Button
                variant="contained"
                onClick={() => advance('PENDING_APPROVAL', 'Verified and sent to Approvals')}
              >
                Verified — send for approval
              </Button>
            ) : (
              <Button variant="contained" onClick={() => advance('COMPLETED', 'Change applied')}>
                Verified — apply change
              </Button>
            )}
            <Button
              color="error"
              onClick={() => advance('REJECTED', 'Request rejected; holder notified')}
            >
              Reject
            </Button>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

export default function ChangesPage() {
  const scope = useTenantScope();
  const [view, setView] = useState<'open' | 'all'>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const rows = useMemo(
    () =>
      scope.changeRequests.filter(
        (item) => view === 'all' || !['COMPLETED', 'REJECTED'].includes(item.status),
      ),
    [scope.changeRequests, view],
  );
  const selected = scope.changeRequests.find((item) => item.id === selectedId) ?? null;
  return (
    <>
      <PageHeader
        title="Transfers & changes"
        subtitle="Transfers, redemptions, transmissions and changes to holder details, from the app, email or the registry team."
      />
      <AboutThisPage>
        <p>
          Investors request most changes in the app, protected by MFA step-up. Address changes apply
          after verification; bank changes, transfers and redemptions also need approval. Transfers
          check the transferee is a wholesale investor and that the trust deed allows the transfer.
        </p>
      </AboutThisPage>
      <Paper variant="outlined">
        <Stack direction="row" spacing={1} sx={{ p: 2 }}>
          <Chip
            label="Open"
            color={view === 'open' ? 'primary' : 'default'}
            variant={view === 'open' ? 'filled' : 'outlined'}
            onClick={() => setView('open')}
          />
          <Chip
            label="All"
            color={view === 'all' ? 'primary' : 'default'}
            variant={view === 'all' ? 'filled' : 'outlined'}
            onClick={() => setView('all')}
          />
        </Stack>
        <DataTable
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) => setSelectedId(row.id)}
          defaultSort={{ key: 'date', direction: 'desc' }}
          dense
          emptyMessage="No open requests"
          columns={[
            { key: 'ref', label: 'Reference', render: (row) => row.reference },
            {
              key: 'kind',
              label: 'Request',
              sortValue: (row) => row.kind,
              render: (row) => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {CHANGE_LABELS[row.kind]}
                </Typography>
              ),
            },
            {
              key: 'holder',
              label: 'Holder',
              render: (row) => scope.holderById.get(row.holderId)?.name,
            },
            {
              key: 'channel',
              label: 'Channel',
              hideBelow: 'md',
              render: (row) => row.channel.replace('_', ' ').toLowerCase(),
            },
            {
              key: 'date',
              label: 'Submitted',
              sortValue: (row) => row.submittedAt,
              render: (row) => date(row.submittedAt),
            },
            { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
          ]}
        />
      </Paper>
      <RequestDrawer
        key={selected?.id ?? 'none'}
        request={selected}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
